import type { CSSProperties, ReactNode, RefObject } from 'react'
import { Fragment, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react'

import type { PublicCalendarEntry, ShareWeekPayload } from '~/api/types'

import { Drawn, Frame, Wordmark, link } from './Frame'
import { Line, Slot, meta } from './Line'
import { shortDay, span } from './night'
import s from './Share.module.css'

export type WeekProps = Omit<ShareWeekPayload, 'kind'>

/** What a night that draws something is worth on the line … */
export const DRAWN = '1fr'
/** … and a night nobody picked on, which keeps its place at half of it for its day name. */
export const QUIET = '0.5fr'

/**
 * The line's seven row weights, Monday→Sunday, off the payload's own nights.
 *
 * Not a rule in the stylesheet, because which of a week's nights are quiet is the *week's* and
 * not the card's: a sheet would need one list per shape of week. A reserved gap has to read as
 * a gap rather than as a hole, and a night carrying nothing but its own day name needs less
 * room to say so than one carrying an act and a room does.
 *
 * A bare `fr` is `minmax(auto, 1fr)`, which is what makes this safe on a doubled night: a night
 * whose picks need more than its share is floored at its own content and the others divide what
 * is left. So a quiet night is half of an *unfloored* drawn night, and not half of a doubled one.
 */
export function nights(days: WeekProps['days']): string {
  return days.map(({ picks }) => (picks.length > 0 ? DRAWN : QUIET)).join(' ')
}

/**
 * The shape of the week the line is drawing: which picks each night carries, in order.
 *
 * The key the card's walk starts over on, and it names the rows rather than counting them.
 * A count is not enough on a card with no cap: the post gives a single act's name a second
 * line when it is too long for its column, so two weeks of the same counts can want
 * different factors, and a card handed the second while holding the first's factor would
 * either stay small or run past its box with nothing to make it look again. The ids are what
 * decide the titles, and a change of shape is a change of ids, so this is the coarser key's
 * condition and the finer one in one string.
 */
export function shape(days: WeekProps['days']): string {
  return days.map(({ picks }) => picks.map(({ id }) => id).join(',')).join(' | ')
}

/** The story's type against the post's — the design's own two week figures in one number. */
export const TYPE = 1.2

/**
 * And the post's, which is the sheet's own base for every card.
 *
 * Both frames' starting factors are stated here rather than one of them being left to the
 * sheet's default, so that one place answers "what is this card drawn at" and it is the same
 * place that answers "how far has it come down".
 */
export const BASE = 1

/** Rounding: a row whose bottom lands this far past the box is on the box, not past it. */
export const SLACK = 0.5

/** What one pass takes when the box measured at nothing, where the ratio cannot answer. */
export const HALVE = 0.5

/**
 * How many times a card may be told to come down before it stops being told.
 *
 * The walk is what makes one imperfect multiplication safe; a bound is what makes the walk
 * safe. A card whose box can never hold it — a frame with nothing left between its head and
 * its foot — would otherwise be measured, shrunk and measured again for as long as React
 * would run the effect. Every pass is strictly smaller than the one before it, so this is a
 * count of passes and not a guess at a size: what it decides is when to stop looking, and
 * the card stops at the smallest factor it reached.
 */
export const PASSES = 8

/** What the nights need of the box they are laid in, and what that box has. */
export interface Box {
  /** How far the last night's bottom is from the line's own top. */
  needed: number
  /** How tall the line's box is. */
  available: number
}

/**
 * Both numbers off one reading of the same two rectangles.
 *
 * The seven rows are `fr` tracks in a box of a fixed height: while they fit, they divide it
 * and the last one ends where the box ends, and when they do not, each is floored at its own
 * content and they run on past it. So "is this card too full" and "by how much" are one
 * subtraction on the last night's own rectangle — the rows are stacked in night order, so
 * the last one's bottom is the furthest anything reaches. One function and one pair of reads,
 * because a second pair would be a second answer to the same question.
 *
 * Measured rather than predicted. A night's height is the sheet's business — how many picks,
 * at what type, with what between them, and on the post whether a name took a second line —
 * and a copy of that arithmetic here would be a second set of numbers to keep in step with
 * the stylesheet, wrong silently the first time a size moves.
 */
export function measure(line: Element): Box | null {
  const last = line.lastElementChild
  if (!last) return null
  const box = line.getBoundingClientRect()
  return { needed: last.getBoundingClientRect().bottom - box.top, available: box.height }
}

/**
 * The next factor for a card that came out taller than its box: what the box has over what
 * the card needed, times the factor it was drawn at.
 *
 * Every length inside the line is written in `--type` — the type, the air between two acts
 * and between two nights, the marker, its stroke and the padding that holds a name off it —
 * so the height the nights need runs with the factor, and one multiplication lands near a
 * card that fits. Near and not on it: a name the post gives a second line to is a step and
 * not a slope, and line boxes round. The caller walks this down until the card fits rather
 * than trusting one pass, which is also what keeps this side of it pure arithmetic.
 *
 * `at` is a factor above zero. A card that fits, within `SLACK`, keeps the factor it has, and
 * so does one whose rectangles cannot answer at all — a `NaN` fails the comparison and is
 * left alone. A box measured at nothing takes `HALVE` instead of the zero the ratio gives,
 * because a factor of zero collapses the card and leaves the next pass nothing to divide.
 */
export function shrink(at: number, needed: number, available: number): number {
  if (!(needed > available + SLACK)) return at
  const next = at * (available / needed)
  return next > 0 ? next : at * HALVE
}

/**
 * The measurement itself, as a child of the frame, because that is where it can be told the
 * card is finished: `Drawn` flips when the faces and every picture that registered itself
 * have arrived, and on the post that includes the foot's wordmark — a foot whose wordmark has
 * not loaded is a foot with no height and a line taller than it will be. The story draws no
 * wordmark, so there is nothing there for it to wait on but the faces, and the faces are
 * what decide the height of everything it does draw.
 *
 * It draws nothing. Each time it is told the card is drawn, or that the card has been drawn
 * at a new factor, it looks again — so a pass that did not take enough off is followed by
 * another. `walk` is stable and refuses a factor that is not smaller than the one in hand,
 * and refuses any factor at all past `PASSES`; on either refusal it hands back the state it
 * already holds, React bails out of the re-render, and nothing looks again.
 */
function Fit({
  line,
  week,
  at,
  walk,
}: {
  line: RefObject<HTMLDivElement | null>
  week: string
  at: number
  walk: (next: number) => void
}) {
  const drawn = useContext(Drawn)
  useLayoutEffect(() => {
    const box = line.current
    if (!drawn || !box) return
    const room = measure(box)
    if (room) walk(shrink(at, room.needed, room.available))
  }, [drawn, week, at, line, walk])
  return null
}

/** The line under a pick's name: the room it is in, and nothing else. */
function room(pick: PublicCalendarEntry): ReactNode {
  return meta(pick, '', { time: false })
}

/**
 * Format A: the carousel's cover — Monday's post, and the same week as a story.
 *
 * The week as a route: one ice line, **a marker a day**, the day in the mono to its left. The
 * seven nights the payload sends are seven rows of the line whether or not each draws
 * anything, so where a marker sits down the rail is which day of the week it is — that is the
 * whole reason the column to the left can be a date axis at all. A night the calendar picked
 * nothing on draws its own day and nothing else — no marker, no name, no room — in the muted
 * grey the card's other secondary lines are in, and keeps its row at a fraction of a drawn
 * one's (`nights` above): the rail is unchanged either way. Naming it is what makes the
 * column readable as an axis without counting rows down from the top, and the grey is what
 * keeps a night with nothing on it from being mistaken for one that has something.
 *
 * A night carrying more than one pick draws **one** marker, stretched into a capsule, with a
 * name and a room beside it per pick. Two markers both labelled `Sat 26` would read as two
 * Saturdays; one marker spanning both reads as what it is.
 *
 * The pick's name is in the reading face and its room is in the mono, and **where that room
 * sits is the difference between the two frames** — the one difference that is a difference of
 * DOM rather than of rule, which is why it is decided here and not in the sheet. On the post
 * the name and the room are two cells of one row, so every room on the card begins at the same
 * x and the names ellipse against that edge; on the story the room is a line under the name it
 * belongs to, inside a block of its own. Both are drawn by the design's own two week figures.
 * The rest of what the frame changes is scale, which the sheet keys on `data-frame`.
 *
 * The head is two nodes in the two faces, and it is also what the renderer reads: `share.PROBES`
 * wants one element drawn in Jost and one in the mono, and both of these are on the card
 * whatever the week holds. A pick's title cannot do that job — a week nobody picked on has none.
 * Which is also why the story's head keeps both nodes while collapsing to one line rather than
 * dropping the headline: the renderer refuses a card whose display probe is missing.
 *
 * **The story has no foot, and that is the second difference of DOM between the frames.** The
 * line is the card; a wordmark and a host line under it are two blocks of the frame the drawing
 * spends on the line instead. Decided here rather than hidden in the sheet, because a foot styled
 * away would still be measured by `Drawn` — the story would go on waiting for a wordmark it
 * does not draw — and because `.week`'s row list has to lose a track with it or the grid keeps
 * the gap that track would have taken.
 *
 * **There is no number of stops this card was drawn for.** It draws what it is given, at
 * whatever factor holds it: `--type` starts at the frame's own base and comes down by the
 * ratio of the box to what the nights needed, for as long as they need more than there is.
 * Nothing else moves with the crowd, because everything the line is written in — the type,
 * the air between two acts and between two nights, the marker, its stroke — is written in
 * that one factor, so the card never chooses between its air and its type. There is no floor
 * under it: past some count the card is unreadable and that is accepted, and what bounds what
 * it is ever handed is the week's own pool of candidates on the server, which is real curated
 * gigs of one week and is checked there against the ids a draft names.
 *
 * The walk starts again at the frame's base whenever the week itself changes — `shape()` and
 * the frame are what "changes" means — so a week that needed the card small does not leave a
 * later one small, and a week that did not is not left overflowing. It is bounded by
 * `PASSES`, which is what a card that can never fit stops at.
 */
export function Week({ host, frame, first, last, days }: WeekProps) {
  const story = frame === 'story'
  const line = useRef<HTMLDivElement>(null)
  const start = story ? TYPE : BASE
  // The factor the card is drawn at, and how many times it has been brought down. The count
  // rides with the factor rather than beside it because they are one decision: a pass is
  // exactly a factor that was accepted, so two pieces of state could disagree about how far
  // the card has come.
  const [drawnAt, setDrawnAt] = useState({ at: start, pass: 0 })
  // Smaller or nothing, and never past `PASSES`. Handing back the same object is what ends
  // the walk: React bails out of a re-render that sets the state it already holds, so the
  // effect below is not run again and nothing measures a card that has settled.
  const walk = useCallback(
    (next: number) =>
      setDrawnAt(now =>
        next < now.at && now.pass < PASSES ? { at: next, pass: now.pass + 1 } : now,
      ),
    [],
  )
  const drawing = `${shape(days)} ${frame ?? ''}`
  // Back to the frame's base when the week itself changes, and *during the render* rather
  // than in an effect. A parent's layout effect runs after its children's, so a reset
  // written as one would fire after `Fit` had already brought the new week down and would
  // put it back — and since the state it wrote is the state it started from, React would
  // stop there and nothing would look again. Adjusting it here re-renders before any effect
  // runs, which is the only ordering in which both halves are true.
  const [seen, setSeen] = useState(drawing)
  if (seen !== drawing) {
    setSeen(drawing)
    setDrawnAt({ at: start, pass: 0 })
  }
  return (
    <Frame
      format="A"
      frame={frame}
      className={s.week}
      // One number, and the sheet writes everything else in terms of it: the type, the air
      // between two acts and between two nights, the marker and its stroke. A second length
      // set here would be a length the card could spend on its own, which is how the air
      // came to disappear from exactly the cards that needed it.
      style={{ '--type': `${drawnAt.at}` } as CSSProperties}
    >
      <div className={s.head}>
        <div className={`${s.big} ${s.disp}`} data-probe="display">
          This week
        </div>
        <div className={`${s.kick} ${s.mono}`} data-probe="data">
          {span(first, last)}
        </div>
      </div>
      <Line labelled ref={line} style={{ gridTemplateRows: nights(days) }}>
        {days.map(({ night, picks }) => (
          <Slot
            key={night}
            night={night}
            // The day's own row says which day it is. Derived from `startsAt` instead, the
            // 01:00 gig that belongs to the night before would label its slot the next day.
            // A night with no row has no `dowShort` to read, so its label is spelled from
            // the night itself — `night.ts`, whose `parts()` goes through UTC, and not a
            // `Date` built here, which is read back in the machine's own zone.
            day={picks[0] ? `${picks[0].dowShort} ${picks[0].dayNum}` : shortDay(night)}
            picks={picks.length}
          >
            {picks.map(pick =>
              story ? (
                <div className={s.pick} key={pick.id}>
                  <div className={s.title}>{pick.title}</div>
                  <span className={`${s.meta} ${s.mono}`}>{room(pick)}</span>
                </div>
              ) : (
                // No wrapper, so the two are cells of the row's own grid and the rooms line up
                // down the card. A `Fragment` and not a `<div className={s.pick}>`: a wrapper
                // here would put the room back under the name in a box of its own.
                <Fragment key={pick.id}>
                  <div className={s.title}>{pick.title}</div>
                  <span className={`${s.meta} ${s.mono}`}>{room(pick)}</span>
                </Fragment>
              ),
            )}
          </Slot>
        ))}
      </Line>
      {story ? null : (
        <div className={s.foot}>
          <div className={`${s.host} ${s.mono}`}>{link(host)}</div>
          <Wordmark width={260} />
        </div>
      )}
      <Fit line={line} week={drawing} at={drawnAt.at} walk={walk} />
    </Frame>
  )
}
